import { useMemo, useCallback, useEffect, useRef } from 'react';
import { useSubscriptionStore } from './store';
import { useCompany } from '@/features/settings/hooks/use-company';
import {
  type SubscriptionFeature,
  type FeatureAccess,
  type CreationAccess,
  FEATURE_REQUIRED_PLAN,
} from './types';
import {
  getFeatureAccess,
  canCreateShop,
  canCreateProduct,
  getPlanTierFromSubscription,
  getNextTierUpgrade,
  getPlanTierDisplayName,
  meetsRequiredTier,
} from './utils';

/**
 * Hook to sync company subscription data with the store
 * Use this at the app root level
 */
export function useSubscriptionSync() {
  const queryResult = useCompany();
  const setSubscription = useSubscriptionStore((state) => state.setSubscription);
  const setCurrentCounts = useSubscriptionStore((state) => state.setCurrentCounts);
  const setLoaded = useSubscriptionStore((state) => state.setLoaded);

  // Track the last synced timestamp to prevent infinite loops
  const lastSyncedAtRef = useRef<number>(0);
  // Store query result in ref to avoid dependency issues
  const queryResultRef = useRef(queryResult);
  queryResultRef.current = queryResult;

  useEffect(() => {
    const { data: company, isLoading, dataUpdatedAt } = queryResultRef.current;

    if (!isLoading && company && dataUpdatedAt > lastSyncedAtRef.current) {
      lastSyncedAtRef.current = dataUpdatedAt;
      setSubscription(company.subscribe ?? null);
      setCurrentCounts(company.shops?.length ?? 0, 0);
      setLoaded(true);
    }
    // Only depend on dataUpdatedAt - a stable timestamp that only changes on actual fetches
  }, [queryResult.dataUpdatedAt, setSubscription, setCurrentCounts, setLoaded]);

  return { isLoading: queryResult.isLoading };
}

/**
 * Hook to get subscription access for a specific feature
 */
export function useSubscriptionFeature(feature: SubscriptionFeature): FeatureAccess {
  const subscription = useSubscriptionStore((state) => state.subscription);
  const currentShopsCount = useSubscriptionStore((state) => state.currentShopsCount);
  const currentProductsCount = useSubscriptionStore((state) => state.currentProductsCount);

  return useMemo(() => {
    return getFeatureAccess(feature, subscription, {
      shops: currentShopsCount,
      products: currentProductsCount,
    });
  }, [feature, subscription, currentShopsCount, currentProductsCount]);
}

/**
 * Hook to check if user can create a shop
 */
export function useCanCreateShop(): CreationAccess {
  const subscription = useSubscriptionStore((state) => state.subscription);
  const currentShopsCount = useSubscriptionStore((state) => state.currentShopsCount);

  return useMemo(() => {
    return canCreateShop(subscription, currentShopsCount);
  }, [subscription, currentShopsCount]);
}

/**
 * Hook to check if user can create a product
 */
export function useCanCreateProduct(): CreationAccess {
  const subscription = useSubscriptionStore((state) => state.subscription);
  const currentProductsCount = useSubscriptionStore((state) => state.currentProductsCount);

  return useMemo(() => {
    return canCreateProduct(subscription, currentProductsCount);
  }, [subscription, currentProductsCount]);
}

/**
 * Hook to check if user can create (generic for shop or product)
 */
export function useCanCreate(type: 'shop' | 'product'): CreationAccess {
  const shopAccess = useCanCreateShop();
  const productAccess = useCanCreateProduct();

  return type === 'shop' ? shopAccess : productAccess;
}

/**
 * Hook to get subscription limits
 */
export function useSubscriptionLimits() {
  const subscription = useSubscriptionStore((state) => state.subscription);
  const currentShopsCount = useSubscriptionStore((state) => state.currentShopsCount);
  const currentProductsCount = useSubscriptionStore((state) => state.currentProductsCount);
  const getShopsLimit = useSubscriptionStore((state) => state.getShopsLimit);
  const getProductsLimit = useSubscriptionStore((state) => state.getProductsLimit);
  const getMetricsLevel = useSubscriptionStore((state) => state.getMetricsLevel);

  return useMemo(
    () => ({
      shops: {
        current: currentShopsCount,
        limit: getShopsLimit(),
        remaining: Math.max(0, getShopsLimit() - currentShopsCount),
      },
      products: {
        current: currentProductsCount,
        limit: getProductsLimit(),
        remaining: Math.max(0, getProductsLimit() - currentProductsCount),
      },
      metricsLevel: getMetricsLevel(),
      planName: subscription?.plan?.name ?? 'Free',
    }),
    [subscription, currentShopsCount, currentProductsCount, getShopsLimit, getProductsLimit, getMetricsLevel]
  );
}

/**
 * Hook to check if user has access to a boolean feature
 */
export function useHasFeatureAccess(feature: SubscriptionFeature): boolean {
  const subscription = useSubscriptionStore((state) => state.subscription);

  return useMemo(() => {
    const planTier = getPlanTierFromSubscription(subscription);
    const requiredPlan = FEATURE_REQUIRED_PLAN[feature];
    return meetsRequiredTier(planTier, requiredPlan);
  }, [feature, subscription]);
}

/**
 * Hook to check multiple feature accesses at once
 */
export function useFeatureAccesses(features: SubscriptionFeature[]): Record<SubscriptionFeature, boolean> {
  const subscription = useSubscriptionStore((state) => state.subscription);

  return useMemo(() => {
    const planTier = getPlanTierFromSubscription(subscription);

    return features.reduce(
      (acc, feature) => {
        const requiredPlan = FEATURE_REQUIRED_PLAN[feature];
        acc[feature] = meetsRequiredTier(planTier, requiredPlan);
        return acc;
      },
      {} as Record<SubscriptionFeature, boolean>
    );
  }, [features, subscription]);
}

/**
 * Hook to get current plan tier and upgrade info
 */
export function usePlanInfo() {
  const subscription = useSubscriptionStore((state) => state.subscription);

  return useMemo(() => {
    const tier = getPlanTierFromSubscription(subscription);
    const nextTier = getNextTierUpgrade(tier);

    return {
      tier,
      tierName: getPlanTierDisplayName(tier),
      nextTier,
      nextTierName: nextTier ? getPlanTierDisplayName(nextTier) : null,
      plan: subscription?.plan ?? null,
      hasSubscription: !!subscription,
      isExpired: subscription ? new Date(subscription.endDate) < new Date() : false,
    };
  }, [subscription]);
}

/**
 * Hook to get specific feature checks (convenience hooks)
 */
export function useCanManageEmployees(): boolean {
  return useSubscriptionStore((state) => state.canManageEmployees());
}

export function useCanAccessAds(): boolean {
  return useSubscriptionStore((state) => state.canAccessAds());
}

export function useCanAccessPriceComparison(): boolean {
  return useSubscriptionStore((state) => state.canAccessPriceComparison());
}

export function useCanDeleteShop(): boolean {
  return useSubscriptionStore((state) => state.canDeleteShop());
}

export function useCanDeleteProduct(): boolean {
  return useSubscriptionStore((state) => state.canDeleteProduct());
}

export function useHasAdvancedMetrics(): boolean {
  return useSubscriptionStore((state) => state.hasAdvancedMetrics());
}

export function useMetricsLevel(): number {
  return useSubscriptionStore((state) => state.getMetricsLevel());
}

/**
 * Hook to get the required plan name for a feature
 */
export function useRequiredPlanForFeature(feature: SubscriptionFeature): string {
  const requiredTier = FEATURE_REQUIRED_PLAN[feature];
  return getPlanTierDisplayName(requiredTier);
}

/**
 * Hook to manually update subscription store
 */
export function useSubscriptionActions() {
  const setSubscription = useSubscriptionStore((state) => state.setSubscription);
  const setCurrentCounts = useSubscriptionStore((state) => state.setCurrentCounts);
  const setLoaded = useSubscriptionStore((state) => state.setLoaded);
  const reset = useSubscriptionStore((state) => state.reset);

  return {
    setSubscription: useCallback(setSubscription, [setSubscription]),
    setCurrentCounts: useCallback(setCurrentCounts, [setCurrentCounts]),
    setLoaded: useCallback(setLoaded, [setLoaded]),
    reset: useCallback(reset, [reset]),
  };
}
